import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type { TFunction } from 'i18next';

import { TrialBannerUrgentPrice } from './components/TrialBannerUrgentPrice/TrialBannerUrgentPrice';
import { TrialBanner } from './TrialBanner';
import { useTrialBanner } from '../../hooks/Billing/useTrialBanner';
import { formatTrialEndDate } from '../../utils/billing/billingFormatters';
import { TrialBannerStage } from '../../utils/billing/getTrialBannerStage';

const CTA_KEY_BY_STAGE: Record<TrialBannerStage, string> = {
  [TrialBannerStage.CALM]: 'billing.trialBanner.calm.cta',
  [TrialBannerStage.WARNING]: 'billing.trialBanner.warning.cta',
  [TrialBannerStage.URGENT]: 'billing.trialBanner.urgent.cta',
  [TrialBannerStage.EXPIRED]: 'billing.trialBanner.expired.cta',
};

function getTitle(
  stage: TrialBannerStage,
  daysLeft: number,
  t: TFunction,
): string {
  if (stage === TrialBannerStage.CALM) {
    return t('billing.trialBanner.calm.title', { count: daysLeft });
  }
  if (stage === TrialBannerStage.WARNING) {
    return t('billing.trialBanner.warning.title', { count: daysLeft });
  }
  if (stage === TrialBannerStage.URGENT) {
    return t('billing.trialBanner.urgent.title');
  }
  return t('billing.trialBanner.expired.title');
}

function getSubtitle(
  stage: TrialBannerStage,
  trialEndsAt: number | undefined,
  teamId: string,
  t: TFunction,
  locale: string,
): ReactNode {
  if (stage === TrialBannerStage.CALM) {
    return t('billing.trialBanner.calm.subtitle', {
      date: trialEndsAt ? formatTrialEndDate(trialEndsAt, locale) : '',
    });
  }
  if (stage === TrialBannerStage.WARNING) {
    return t('billing.trialBanner.warning.subtitle');
  }
  if (stage === TrialBannerStage.URGENT) {
    return <TrialBannerUrgentPrice teamId={teamId} />;
  }
  return t('billing.trialBanner.expired.subtitle');
}

export function TrialBannerContainer() {
  const { t, i18n } = useTranslation();
  const {
    isVisible,
    stage,
    daysLeft,
    trialEndsAt,
    teamId,
    isDismissible,
    canManage,
    dismiss,
    goToBilling,
  } = useTrialBanner();

  if (!isVisible || !stage || !teamId) {
    return null;
  }

  return (
    <TrialBanner
      stage={stage}
      title={getTitle(stage, daysLeft, t)}
      subtitle={getSubtitle(stage, trialEndsAt, teamId, t, i18n.language)}
      ctaLabel={
        canManage
          ? t(CTA_KEY_BY_STAGE[stage])
          : t('billing.trialBanner.memberSubtitle')
      }
      onCtaClick={canManage ? goToBilling : undefined}
      isDismissible={isDismissible}
      onDismiss={dismiss}
      dismissAriaLabel={t('billing.trialBanner.dismiss')}
    />
  );
}
